import { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import { isAuthenticated } from '@/lib/auth'
import { makeStyles } from '@/hooks/makeStyles'
import { COLORS } from '@/constants/colors'

const LoginPage = lazy(() => import('@/features/auth/LoginPage'))
const DashboardRoute = lazy(() => import('@/pages/DashboardRoute'))
const WorkspaceRoute = lazy(() => import('@/pages/WorkspaceRoute'))
const ProjectsRoute = lazy(() => import('@/pages/ProjectsRoute'))
const AwgmentHealthRoute = lazy(() => import('@/pages/AwgmentHealthRoute'))
const SettingsRoute = lazy(() => import('@/pages/SettingsRoute'))
const SourcesRoute = lazy(() => import('@/pages/SourcesRoute'))
const DestinationsRoute = lazy(() => import('@/pages/DestinationsRoute'))
const ConnectionsRoute = lazy(() => import('@/pages/ConnectionsRoute'))
const SemanticLayerRoute = lazy(() => import('@/pages/SemanticLayerRoute'))

const useStyles = makeStyles((_theme) => ({
  spinnerWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: COLORS?.BG_DARK,
  },
  spinner: {
    color: COLORS?.PRIMARY,
  },
}))

function FullPageSpinner() {
  const classes = useStyles()
  return (
    <Box className={classes?.spinnerWrapper}>
      <CircularProgress className={classes?.spinner} />
    </Box>
  )
}

interface ProtectedProps {
  children: React.ReactNode
}

function ProtectedRoute({ children }: ProtectedProps) {
  if (!isAuthenticated()) {
    return <Navigate to='/login' replace />
  }
  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<FullPageSpinner />}>
        <Routes>
          {/* Public */}
          <Route path='/login' element={<LoginPage />} />

          {/* Root redirect */}
          <Route path='/' element={<Navigate to='/dashboard' replace />} />

          {/* Protected */}
          <Route
            path='/dashboard'
            element={
              <ProtectedRoute>
                <DashboardRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path='/workspace/:projectId'
            element={
              <ProtectedRoute>
                <WorkspaceRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path='/workspace'
            element={
              <ProtectedRoute>
                <WorkspaceRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path='/projects'
            element={
              <ProtectedRoute>
                <ProjectsRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path='/awgment-health'
            element={
              <ProtectedRoute>
                <AwgmentHealthRoute />
              </ProtectedRoute>
            }
          />
          {/* <Route
            path='/settings'
            element={
              <ProtectedRoute>
                <SettingsRoute />
              </ProtectedRoute>
            }
          /> */}
          <Route
            path='/sources'
            element={
              <ProtectedRoute>
                <SourcesRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path='/destinations'
            element={
              <ProtectedRoute>
                <DestinationsRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path='/connections'
            element={
              <ProtectedRoute>
                <ConnectionsRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path='/semantic-layer'
            element={
              <ProtectedRoute>
                <SemanticLayerRoute />
              </ProtectedRoute>
            }
          />

          {/* Catch-all */}
          <Route
            path='*'
            element={
              isAuthenticated() ? (
                <Navigate to='/dashboard' replace />
              ) : (
                <Navigate to='/login' replace />
              )
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
